<template>
  <div>
    <v-list class="pa-0" nav color="transparent">
      <v-list-item-group :dense="$vuetify.breakpoint.xs" v-model="activeListItem">
        <!-- Friends -->
        <template v-if="!userIsBlockedByProfile && !profileIsBlockedByUser">
          <section v-if="!userOwnsProfile" :key="friendRequestStatus">
            <v-list-item v-if="friendRequestStatus === 'approved'" @click="activateRemoveFriendDialog(profileId)">
              <v-list-item-icon>
                <v-icon> mdi-account-check </v-icon>
              </v-list-item-icon>

              <v-list-item-title> Already Friends </v-list-item-title>
            </v-list-item>

            <v-list-item v-else-if="friendRequestStatus === 'outgoing'" @click="activateRemoveFriendDialog(profileId)">
              <v-list-item-icon>
                <v-icon> mdi-account-question </v-icon>
              </v-list-item-icon>

              <v-list-item-title> Friend Request Sent </v-list-item-title>
            </v-list-item>

            <v-list-item
              v-else-if="friendRequestStatus === null || friendRequestStatus === 'incoming'"
              active-class="no-active"
              @click.native="addFriendAndUpdateStatus(profileUsername)"
            >
              <v-list-item-icon>
                <v-icon> mdi-account-plus </v-icon>
              </v-list-item-icon>

              <v-list-item-title> Add Friend </v-list-item-title>
            </v-list-item>
          </section>

          <!-- Friend List -->
          <v-list-item
            :to="{
              name: 'contacts',
              params: { username: profileUsername },
            }"
            active-class="no-active"
          >
            <v-list-item-icon>
              <v-icon> mdi-account-group </v-icon>
            </v-list-item-icon>

            <v-list-item-title> Friend List </v-list-item-title>
          </v-list-item>

          <v-list-item
            :to="{
              name: 'works-engaged',
              params: { username: profileUsername },
            }"
            active-class="no-active"
          >
            <v-list-item-icon>
              <v-icon> mdi-archive-check </v-icon>
            </v-list-item-icon>

            <v-list-item-title> Works Engaged </v-list-item-title>
          </v-list-item>
        </template>

        <v-list-item v-if="userOwnsProfile" :to="{ name: 'settings' }" active-class="no-active">
          <v-list-item-icon>
            <v-icon> mdi-cog </v-icon>
          </v-list-item-icon>

          <v-list-item-title> Settings </v-list-item-title>
        </v-list-item>

        <template v-if="username && !userOwnsProfile">
          <v-list-item @click="toggleBlocked" active-class="active-list-item">
            <v-list-item-icon>
              <v-icon class="red--text">mdi-block-helper</v-icon>
            </v-list-item-icon>

            <v-list-item-title>
              <span class="red--text" v-if="!profileIsBlockedByUser">Block User</span>
              <span v-else class="red--text lighten-1">Blocked</span>
            </v-list-item-title>
          </v-list-item>

          <v-list-item
            @click="
              $emit('report-user');
              activeListItem = null;
            "
          >
            <v-list-item-icon>
              <v-icon class="red--text">mdi-flag-variant</v-icon>
            </v-list-item-icon>

            <v-list-item-title class="red--text"> Report User </v-list-item-title>
          </v-list-item>
        </template>
      </v-list-item-group>
    </v-list>

    <!-- TODO: Refactor dialog card into separate component -->
    <v-dialog v-model="blockDialog.active" max-width="500px">
      <v-card>
        <p class="pa-4">Are you sure that you want to block {{ profileUsername }}?</p>

        <v-card-actions>
          <v-btn @click="blockDialog.active = false"> No </v-btn>

          <v-spacer></v-spacer>

          <v-btn @click="blockUserAndUpdateStatus" color="primary"> Yes </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="removeFriendDialog.active" max-width="500px">
      <v-card>
        <p class="pa-4">
          <span v-if="friendRequestStatus === 'outgoing'">Cancel your friend request to {{ profileUsername }}?</span>
          <span v-else>Remove {{ profileUsername }} from your friends?</span>
        </p>

        <v-card-actions>
          <v-btn @click="removeFriendDialog.active = false"> No </v-btn>

          <v-spacer></v-spacer>

          <v-btn @click="removeFriendAndUpdateStatus()" color="primary"> Yes </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapMutations } from "vuex";

import friends from "../../mixins/friends";
import blockedUsers from "../../mixins/blockedUsers";

export default {
  name: "profile-utility-buttons.vue",

  mixins: [friends, blockedUsers],

  props: {
    profileUsername: String,
    profileId: Number,
    userIsBlockedByProfile: {
      type: Boolean,
      default: false,
    },
    profileIsBlockedByUser: {
      type: Boolean,
      default: false,
    },
    profileIsFriend: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    username() {
      return this.$store.state.username;
    },

    userOwnsProfile() {
      return this.username === this.profileUsername;
    },
  },

  watch: {
    profileIsFriend: async function (newValue) {
      if (newValue) {
        this.friendRequestStatus = "approved";
      } else {
        this.friendRequestStatus = null;
      }
    },

    profileId: async function () {
      await this.retrieveFriendRequestStatus();
    },
  },

  data() {
    return {
      friendRequestStatus: null,

      blockDialog: {
        active: false,
      },

      removeFriendDialog: {
        active: false,
      },

      activeListItem: null,
    };
  },

  async mounted() {
    if (this.profileId) {
      await this.retrieveFriendRequestStatus();
    }
  },

  methods: {
    ...mapMutations(["showSnackbar"]),

    retrieveFriendRequestStatus: async function () {
      this.friendRequestStatus = await this.getFriendshipStatus(this.profileId);
    },

    addFriendAndUpdateStatus: async function () {
      const result = await this.addFriendById(this.profileId);
      this.friendRequestStatus = result.requestStatus;
      this.$emit("add-friend", { requestStatus: this.friendRequestStatus });
    },

    toggleBlocked: async function () {
      if (this.profileIsBlockedByUser) {
        await this.unblockUser(this.profileUsername);
        this.$emit("unblock-user", {
          username: this.profileUsername,
        });
      } else {
        this.blockDialog.active = true;
      }
      this.activeListItem = null;
    },

    activateRemoveFriendDialog: async function () {
      this.removeFriendDialog.active = true;
    },

    removeFriendAndUpdateStatus: async function () {
      try {
        await this.removeFriendById(this.profileId);
        this.friendRequestStatus = null;
        this.removeFriendDialog.active = false;
        this.$emit("remove-friend", {
          username: this.profileUsername,
        });
        this.showSnackbar({ text: "Friend successfully removed" });
      } catch {
        this.showSnackbar({ text: "An error occurred", error: true });
      }
    },

    blockUserAndUpdateStatus: async function () {
      try {
        await this.blockUser(this.profileUsername);
        console.log("blocked");
        this.showSnackbar({ text: "Blocked" });
        this.blockDialog.active = false;
        this.friendRequestStatus = null;
        this.$emit("block-user", {
          username: this.profileUsername,
        });
      } catch {
        this.showSnackbar({ text: "An error occurred", error: true });
      }
    },
  },
};
</script>

<style scoped>
.v-list-item {
  min-height: 24px;
  padding: 4px 0;
  margin-bottom: 0 !important;
}

@media (min-width: 600px) {
  .v-list-item {
    min-height: 48px;
  }
}

.v-list-item__icon {
  margin: auto 0;
}

.active-list-item {
  background-color: var(--v-card-base);
}
</style>
